/**
 * ContactDrawer — rechter-drawer voor één contactpersoon (Golf 3).
 *
 * Zelfde visuele patroon als TimelineItemDrawer: backdrop + panel rechts.
 * Laadt het detail via GET /api/customers/contact-detail (mirror-first):
 * naam, functie, afdeling, e-mail, telefoon, opt-out en de orders van die
 * persoon (mirror_werk_order op contactpersoon). De bewerk-knop delegeert
 * naar de bestaande EditContactDialog via onEdit.
 */
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { formatCurrency, formatDate } from '../../lib/format';

export interface ContactDrawerOrder {
  id: string;
  reference: string;
  status: string;
  brand: string;
  total: number;
  openAmount: number;
  paymentReceived: boolean;
  pickupDate: string | null;
  deliveryDate: string | null;
  createdDate: string;
}

export interface ContactDrawerDetail {
  id: string;
  accountId: string;
  accountName: string;
  name: string;
  email: string;
  phone: string;
  mobilePhone: string;
  title: string;
  department: string;
  emailOptOut: boolean;
}

interface ContactDrawerProps {
  /** SF Contact-id; null = drawer dicht. */
  contactId: string | null;
  onClose: () => void;
  onEdit?: (detail: ContactDrawerDetail) => void;
  onOpenOrder?: (orderId: string) => void;
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-700 break-words">{value}</dd>
    </>
  );
}

export function ContactDrawer({ contactId, onClose, onEdit, onOpenOrder }: ContactDrawerProps) {
  const [detail, setDetail] = useState<ContactDrawerDetail | null>(null);
  const [orders, setOrders] = useState<ContactDrawerOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!contactId) {
      setDetail(null);
      setOrders([]);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          if (!cancelled) setError('Niet ingelogd');
          return;
        }
        const res = await fetch(`/api/customers/contact-detail?id=${encodeURIComponent(contactId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: 'Ophalen mislukt' }));
          if (!cancelled) setError(err.error || 'Ophalen mislukt');
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setDetail(data.contact);
          setOrders(data.orders || []);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Netwerkfout');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [contactId]);

  // Esc sluit.
  useEffect(() => {
    if (!contactId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [contactId, onClose]);

  if (!contactId) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-900/30 z-40" onClick={onClose} aria-hidden />
      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-[560px] bg-white z-50 shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 flex-shrink-0">
          <button onClick={onClose} aria-label="Sluiten" className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-sm font-semibold text-slate-900 truncate flex-1">
            {detail?.name || (loading ? 'Laden…' : 'Contactpersoon')}
          </h2>
          {detail?.emailOptOut && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700">
              Geen marketing-mail
            </span>
          )}
          {detail && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(detail)}
              className="px-2.5 py-1 text-xs font-medium text-indigo-600 border border-indigo-200 rounded hover:bg-indigo-50"
            >
              Bewerken
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-6 space-y-3 animate-pulse">
              <div className="h-3 bg-slate-100 rounded w-2/3" />
              <div className="h-3 bg-slate-100 rounded w-1/2" />
              <div className="h-3 bg-slate-100 rounded w-3/4" />
              <div className="h-40 bg-slate-100 rounded mt-4" />
            </div>
          )}
          {error && (
            <div className="m-5 px-4 py-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
              {error}
            </div>
          )}
          {detail && !loading && !error && (
            <>
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
                <dl className="grid grid-cols-[90px_1fr] gap-y-1 text-xs">
                  {detail.accountName && <MetaRow label="Bedrijf" value={detail.accountName} />}
                  {detail.title && <MetaRow label="Functie" value={detail.title} />}
                  {detail.department && <MetaRow label="Afdeling" value={detail.department} />}
                  {detail.email && <MetaRow label="E-mail" value={detail.email} />}
                  {detail.phone && <MetaRow label="Telefoon" value={detail.phone} />}
                  {detail.mobilePhone && <MetaRow label="Mobiel" value={detail.mobilePhone} />}
                </dl>
              </div>

              {/* Orders van deze contactpersoon */}
              <div className="px-5 py-3">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Orders van deze persoon ({orders.length})
                </h3>
                {orders.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">Geen orders op deze contactpersoon.</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
                    {orders.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onClick={() => onOpenOrder?.(o.id)}
                        className={clsx(
                          'block w-full text-left px-3 py-2 transition-colors',
                          onOpenOrder ? 'hover:bg-indigo-50/40 cursor-pointer' : 'cursor-default'
                        )}
                      >
                        <span className="flex items-center justify-between mb-0.5">
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="text-xs font-medium text-slate-900">{o.reference}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-100 text-slate-600 truncate">
                              {o.status}
                            </span>
                          </span>
                          <span className="text-xs font-medium text-slate-900 flex-shrink-0">
                            {formatCurrency(o.total)}
                          </span>
                        </span>
                        <span className="flex items-center gap-3 text-[11px] text-slate-400">
                          <span>{formatDate(o.createdDate)}</span>
                          {o.paymentReceived ? (
                            <span className="text-emerald-600">Betaald</span>
                          ) : o.openAmount > 0 ? (
                            <span className="text-amber-600">Open {formatCurrency(o.openAmount)}</span>
                          ) : null}
                          {o.pickupDate && <span>Ophalen {formatDate(o.pickupDate)}</span>}
                          {o.deliveryDate && <span>Bezorgen {formatDate(o.deliveryDate)}</span>}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
